import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Trophy, Skull, ChevronRight, Trash2 } from 'lucide-react';
import { listGames, deleteGame } from '@/api/mafia';
import { toast } from 'sonner';
import { useTranslation } from '@/i18n/LanguageContext';

const RED = '#DC2626';

export default function MafiaHistoryPage() {
 const navigate = useNavigate();
 const { t, tWinner } = useTranslation();
 const [games, setGames] = useState([]);
 const [loading, setLoading] = useState(true);

 const teamColor = (team) => {
 if (team === 'red') return 'text-rose-300';
 if (team === 'black') return 'text-zinc-200';
 if (team === 'gray') return 'text-violet-300';
 return 'text-[#F5F5F5]';
 };

 const load = async () => {
 setLoading(true);
 try {
 const data = await listGames();
 setGames(data.games || []);
 } catch (e) {
 toast.error(t('game.toast-load-failed'));
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 load();
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []);

 const handleDelete = async (id) => {
 if (!window.confirm(t('history.confirm-delete'))) return;
 try {
 await deleteGame(id);
 toast.success(t('history.deleted'));
 load();
 } catch (e) {
 toast.error('Failed to delete');
 }
 };

 const active = games.filter((g) => g.status !== 'ended');
 const ended = games.filter((g) => g.status === 'ended');

 return (
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 className="min-h-screen pt-16 pb-32 px-6 max-w-5xl mx-auto"
 data-testid="mafia-history-page"
 >
 <button
 onClick={() => navigate('/mafia')}
 className="flex items-center gap-2 text-[#A3A3A3] hover:text-[#DC2626] mb-6 transition-colors"
 >
 <ArrowLeft size={16} /> {t('common.back')}
 </button>

 <h1 className="font-serif text-3xl md:text-4xl text-[#F5F5F5] mb-2">{t('history.title')}</h1>
 <p className="text-[#A3A3A3] text-sm mb-8">{t('history.subtitle')}</p>

 {loading && <p className="text-[#A3A3A3]">{t('common.loading')}</p>}

 {!loading && active.length > 0 && (
 <section className="mb-10">
 <h2 className="font-serif text-xl mb-3 flex items-center gap-2" style={{ color: RED }}>
 {t('history.active')}
 </h2>
 <div className="space-y-2">
 {active.map((g) => (
 <div
 key={g.id}
 className="flex items-center gap-3 p-4 border border-white/10 bg-[#0A0505]"
 data-testid={`history-active-${g.id}`}
 >
 <button
 onClick={() => navigate(`/mafia/game/${g.id}`)}
 className="flex-1 text-left"
 >
 <p className="text-[#F5F5F5]">{g.name}</p>
 <p className="text-xs text-[#A3A3A3] mt-1">
 {g.player_count} {t('common.players')} · {g.status === 'setup' ? t('common.setup') : `${t('common.round')} ${g.current_round} · ${t(`game.phase.${g.current_phase}`)}`}
 </p>
 </button>
 <button
 onClick={() => handleDelete(g.id)}
 className="p-2 rounded-lg hover:bg-white/5 text-[#A3A3A3] hover:text-rose-400"
 >
 <Trash2 size={14} />
 </button>
 <ChevronRight size={18} style={{ color: RED }} />
 </div>
 ))}
 </div>
 </section>
 )}

 {!loading && ended.length > 0 && (
 <section>
 <h2 className="font-serif text-xl text-[#A3A3A3] mb-3 flex items-center gap-2">
 <Skull size={16} /> {t('history.finished')}
 </h2>
 <div className="space-y-2">
 {ended.map((g) => (
 <div
 key={g.id}
 className="flex items-center gap-3 p-4 border border-white/5 bg-[#0A0505]/60"
 data-testid={`history-ended-${g.id}`}
 >
 <button
 onClick={() => navigate(`/mafia/game/${g.id}`)}
 className="flex-1 text-left"
 >
 <p className="text-[#F5F5F5]/80">{g.name}</p>
 <p className="text-xs text-[#A3A3A3] mt-1 flex items-center gap-2">
 <Trophy size={11} /> {t('common.winners')}: <span className={`uppercase tracking-wider ${teamColor(g.winners)}`}>{tWinner(g.winners)}</span> · {g.player_count} {t('common.players')} · {(g.ended_at || '').slice(0, 10)}
 </p>
 </button>
 <button
 onClick={() => handleDelete(g.id)}
 className="p-2 rounded-lg hover:bg-white/5 text-[#A3A3A3] hover:text-rose-400"
 >
 <Trash2 size={14} />
 </button>
 <ChevronRight size={18} className="text-[#A3A3A3]" />
 </div>
 ))}
 </div>
 </section>
 )}

 {!loading && games.length === 0 && (
 <div className="text-center py-16">
 <Skull size={48} className="mx-auto mb-4 text-[#A3A3A3]/40" />
 <p className="text-[#A3A3A3]">{t('history.no-games')}</p>
 </div>
 )}
 </motion.div>
 );
}
